/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import {
  DataTable,
  Modal,
  Search,
  ToggleSwitch,
  type DataTableColumn,
} from '@enamelai/design-system'
import { getAllProcedures } from '@services/api/procedure.api'
import { logger } from '@utils/safeLogger'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'

type AddEditFavoriteProps = {
  show: boolean
  onHide: () => void
  actions: any
  /** Current favorite records; each has `procedure_id` and `common_proc_id`. */
  favoriteProcedures: any[]
}

type FavoriteRow = {
  id: string
  favorite: boolean
  code: string
  description: string
}

const readSortValue = (row: FavoriteRow, columnId: string): string =>
  columnId === 'favorite'
    ? row.favorite
      ? '0'
      : '1'
    : String(row[columnId as keyof FavoriteRow] ?? '').toLowerCase()

const AddEditFavorite: React.FC<AddEditFavoriteProps> = ({
  show,
  onHide,
  actions,
  favoriteProcedures = [],
}: AddEditFavoriteProps): React.ReactElement => {
  const [procedures, setProcedures] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sort, toggleSort] = useTableSort()

  const savedIds = useMemo(
    () => new Set<string>(favoriteProcedures.map((f) => f.procedure_id)),
    [favoriteProcedures],
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(savedIds),
  )

  useEffect(() => {
    getAllProcedures(true, true)
      .then((res) => {
        if ('procedures' in res.data) setProcedures(res.data.procedures || [])
      })
      .catch((error) => logger.error('Error fetching procedures:', error))
      .finally(() => setLoading(false))
  }, [])

  const toggleFavorite = (procedureId: string, next: boolean): void => {
    setSelectedIds((prev) => {
      const updated = new Set(prev)
      if (next) updated.add(procedureId)
      else updated.delete(procedureId)
      return updated
    })
  }

  const rows: FavoriteRow[] = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return procedures
      .filter(
        (p) =>
          !query ||
          [p.procedure_code, p.description].some((field) =>
            field?.toString().toLowerCase().includes(query),
          ),
      )
      .sort((a, b) => a.procedure_code.localeCompare(b.procedure_code))
      .map((p) => ({
        id: p.procedure_id,
        favorite: selectedIds.has(p.procedure_id),
        code: p.procedure_code,
        description: p.description ?? '',
      }))
  }, [procedures, searchQuery, selectedIds])

  const sortedRows = sortRows(rows, sort, readSortValue)
  const { pageRows, pagination } = usePagedRows(sortedRows)

  const columns: DataTableColumn<FavoriteRow>[] = [
    {
      id: 'favorite',
      label: 'Favorite',
      sortable: true,
      width: 120,
      renderCell: (row) => (
        <ToggleSwitch
          aria-label={`Favorite ${row.code}`}
          checked={row.favorite}
          onChange={(checked) => toggleFavorite(row.id, checked)}
        />
      ),
    },
    { id: 'code', label: 'Procedure code', sortable: true, width: 160 },
    { id: 'description', label: 'Description', sortable: true },
  ]

  const handleSaveClick = async (): Promise<void> => {
    const toAdd = Array.from(selectedIds).filter((id) => !savedIds.has(id))
    const toRemove = favoriteProcedures
      .filter((f) => !selectedIds.has(f.procedure_id))
      .map((f) => f.common_proc_id)
    if (toAdd.length === 0 && toRemove.length === 0) {
      onHide()
      return
    }
    await actions.saveMany(toAdd, toRemove)
  }

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="lg"
      title="Add favorite procedure"
      closeOnBackdrop={false}
      className="bf-modal bf-modal--favorite"
      secondaryLabel="Cancel"
      onSecondaryClick={onHide}
      primaryLabel="Save"
      onPrimaryClick={() => {
        void handleSaveClick()
      }}
    >
      <div className="bf-modal__favorite">
        <Search
          className="bf-modal__search"
          placeholder="Search"
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
        <DataTable<FavoriteRow>
          className="bf-favorite-table"
          columns={columns}
          rows={pageRows}
          state={loading ? 'loading' : rows.length === 0 ? 'empty' : 'default'}
          emptyState={{
            title: 'No procedures found',
            description: searchQuery
              ? 'No procedures match your search.'
              : 'Activate procedure codes to add them as favorites.',
          }}
          sortColumnId={sort.columnId}
          sortDirection={sort.columnId ? sort.direction : undefined}
          onSort={toggleSort}
          showActions={false}
          pagination={pagination}
        />
      </div>
    </Modal>
  )
}

export default AddEditFavorite
